import { Link } from "@inertiajs/react";
import { Target } from "lucide-react";
import { useForm } from "@inertiajs/react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";
import ProgressBar from "@/components/shared/ProgressBar";

function PersonalGoalsPreview() {
  const { data, setData } = useForm({ minutes: "500", money: "100" });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          Personal goals
        </CardTitle>
        <CardDescription>Optional targets you set with your child. Private to your family.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField name="minutes" label="Minute goal" type="number" value={data.minutes} onChange={(e) => setData("minutes", e.target.value)} />
          <FormField name="money" label="Fundraising goal ($)" type="number" value={data.money} onChange={(e) => setData("money", e.target.value)} />
        </div>
        <ProgressBar label="Reading" detail={`135 of ${data.minutes || "—"} minutes`} pct={data.minutes ? Math.round((135 / Number(data.minutes)) * 100) : 0} />
        <Button variant="outline">Save goals</Button>
      </CardContent>
    </Card>
  );
}

export default function SettingPersonalGoals() {
  return (
    <>
      <p>
        You can set a private <strong>personal goal</strong> for your child — a minute target, a money target, or both. It's just between you and them; no one else sees it, and no prize is attached.
      </p>

      <h2>Where to find it</h2>
      <p>Click your child's name in your sidebar. The personal goal card sits between their stats and the school's progress.</p>
      <Screenshot caption="The personal goal card">
        <div className="max-w-md mx-auto">
          <PersonalGoalsPreview />
        </div>
      </Screenshot>

      <p>
        Set it with your child, not for them — "Want to try to read 500 minutes by the end?" is a much more motivating conversation than typing a number and surprising them. The agreement matters more than the number.
      </p>

      <Callout variant="tip" title="What's a reasonable goal?">
        <p>
          Most children read 15–20 minutes a day already. Multiply by how long the campaign runs for a baseline, then add 30–50% as a stretch. A two-week read-a-thon at 20 minutes a day is 280 minutes — a 400-minute goal would feel like a stretch.
        </p>
      </Callout>

      <p>
        Once set, you'll see a progress bar on your child's page, and a small celebration when they cross the line. No prize comes with it — these are just for your family. Edit or remove the goal any time.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/logging-minutes-at-home">Logging reading from home</Link>.</li>
        <li><Link href="/help/articles/leaderboards-and-privacy">Leaderboards and your child's privacy</Link>.</li>
      </ul>
    </>
  );
}
